import { apiFetch } from "../Utility/api";

export const getNowLocalDateTime = () => {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 19);
};

export const parseLocalDateTimeInput = (dateTimeValue) => (
  dateTimeValue ? new Date(dateTimeValue) : null
);

export const calculateMinutesBetween = (startValue, endValue) => {
  const startTime = parseLocalDateTimeInput(startValue);
  const endTime = parseLocalDateTimeInput(endValue);
  if (!startTime || !endTime || Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) {
    return "";
  }
  const seconds = Math.max(0, Math.round((endTime.getTime() - startTime.getTime()) / 1000));
  return String(seconds / 60);
};

export const formatElapsedSeconds = (elapsedSeconds) => {
  const safeSeconds = Math.max(0, elapsedSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const formatDurationFromMinutes = (minuteValue) => {
  if (minuteValue === null || minuteValue === undefined || minuteValue === "") return "";
  const totalSeconds = Math.max(0, Math.round(Number(minuteValue) * 60));
  if (Number.isNaN(totalSeconds)) return "";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes}m ${seconds}s`;
};

export const formatFeedingAmount = (entryOrForm) => {
  if (!entryOrForm?.feedingAmount) return "";
  if (normalizeFeedingType(entryOrForm.eventType) === "Breast" || entryOrForm.feedingUnit === "min") {
    return formatDurationFromMinutes(entryOrForm.feedingAmount);
  }
  return `${entryOrForm.feedingAmount} ${entryOrForm.feedingUnit || ""}`.trim();
};

export const eventCategoryLabels = {
  diaper: "Diaper",
  feeding: "Feeding",
  pumping: "Pumping",
  medicine: "Medicine",
  sleep: "Sleep",
};

export const eventCategoryPluralLabels = {
  diaper: "Diapers",
  feeding: "Feedings",
  pumping: "Pumping",
  medicine: "Medicine",
};

export const diaperTypes = ["Wet", "Dirty", "Mixed", "Dry/Checked"];
export const feedingTypes = ["Breast", "Bottle"];
export const pumpingTypes = ["Pumping"];
export const medicineTypes = ["Gas Relief Drops", "Probiotics", "Gripe Water", "Vitamin D Drops", "Other"];
export const defaultBottleMilkType = "Breast Milk";
export const bottleMilkTypes = [defaultBottleMilkType, "Formula"];
export const sleepTypes = ["Sleep", "Wake Up"];
export const feedingSides = ["", "Left", "Right"];

export const feedingUnitForType = (eventType) => (eventType === "Breast" ? "min" : "oz");
export const normalizeFeedingType = (eventType) => (
  eventType === "Bottle Formula" || eventType === "Bottle Milk" ? "Bottle" : eventType
);

export const bottleMilkTypeForEvent = (event) => {
  if (event?.feedingMilkType) return event.feedingMilkType;
  if (event?.eventType === "Bottle Milk") return "Breast Milk";
  if (event?.eventType === "Bottle Formula") return "Formula";
  return "Formula";
};

export const sleepEventDisplay = (eventType) => {
  if (eventType === "Wake Up") {
    return {
      label: "Woke up",
      detail: "Wake time",
      tone: "wake",
    };
  }
  return {
    label: "Sleep started",
    detail: "Sleep time",
    tone: "sleep",
  };
};

export const defaultBabyEventForm = (category = "diaper", type = "Wet") => ({
  eventCategory: category,
  eventType: type,
  occurredAt: getNowLocalDateTime(),
  endedAt: "",
  feedingAmount: "",
  feedingUnit: feedingUnitForType(type),
  feedingMilkType: type === "Bottle" ? defaultBottleMilkType : "",
  feedingSide: "",
  medicineOtherName: "",
  notes: "",
});

export const eventTypeOptionsForCategory = (category) => {
  if (category === "feeding") return feedingTypes;
  if (category === "pumping") return pumpingTypes;
  if (category === "medicine") return medicineTypes;
  if (category === "sleep") return sleepTypes;
  return diaperTypes;
};

export const fetchBabySummary = async () => {
  const response = await apiFetch("/baby-tracker/summary");
  if (!response.ok) {
    throw new Error("Unable to load baby summary.");
  }
  return response.json();
};

export const fetchBabyEvents = async (category = "", limit = 60) => {
  const queryParams = new URLSearchParams();
  if (category) queryParams.set("category", category);
  if (limit) queryParams.set("limit", String(limit));
  const queryString = queryParams.toString();
  const response = await apiFetch(`/baby-tracker/events${queryString ? `?${queryString}` : ""}`);
  return response.json();
};

export const fetchBabyFeedingHistory = async (date) => {
  const queryParams = new URLSearchParams();
  if (date) queryParams.set("date", date);
  const queryString = queryParams.toString();
  const response = await apiFetch(`/baby-tracker/feeding-history-data${queryString ? `?${queryString}` : ""}`);
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Sign in again to load feeding history.");
    }
    if (response.status === 404) {
      throw new Error("Feeding history API is not available on the server yet.");
    }
    throw new Error(`Unable to load feeding history. Server returned ${response.status}.`);
  }
  return response.json();
};

export const saveBabyEvent = async (eventForm, editingId = null) => {
  const response = await apiFetch(
    editingId ? `/baby-tracker/events/${editingId}` : "/baby-tracker/events",
    {
      method: editingId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventForm),
    }
  );
  return response.json();
};

export const saveBabyEventNotes = async (eventId, notes) => {
  const response = await apiFetch(`/baby-tracker/events/${eventId}/notes`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ notes }),
  });
  if (!response.ok) {
    throw new Error("Unable to save notes.");
  }
  return response.json();
};

export const deleteBabyEvent = async (eventId) => {
  const response = await apiFetch(`/baby-tracker/events/${eventId}`, {
    method: "DELETE",
  });
  return response.json();
};

export const fetchMedicineReminders = async () => {
  const response = await apiFetch("/baby-tracker/medicine-reminders");
  return response.json();
};

export const saveMedicineReminder = async (reminder, reminderId = null) => {
  const response = await apiFetch(
    reminderId ? `/baby-tracker/medicine-reminders/${reminderId}` : "/baby-tracker/medicine-reminders",
    {
      method: reminderId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reminder),
    }
  );
  if (!response.ok) {
    throw new Error("Unable to save medicine reminder.");
  }
  return response.json();
};

export const deleteMedicineReminder = async (reminderId) => {
  const response = await apiFetch(`/baby-tracker/medicine-reminders/${reminderId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new Error("Unable to delete medicine reminder.");
  }
  return response.json();
};
